import { defineStore, acceptHMRUpdate } from 'pinia';
import keyBy from 'lodash/keyBy';
import { addStar, deleteStar, fetchStar, fetchStars } from '@/stores/api/stars.api';

export interface Star {
    id?: string;
    itemId: string;
    itemType: string;
    starred: boolean;
    createdAt?: number;
    updatedAt?: number;
}

interface StarsState {
    error: unknown;
    fetching: boolean;
    map: Record<string, Star>;
}

export function getInitialState (): StarsState {
    return {
        error: null,
        fetching: false,
        map: {},
    };
}

// Module-level deduplication — not store state; reset via __resetLoadAllPromiseForUnitTests
let loadAllPromise: Promise<void> | null = null;

export const useStarsStore = defineStore('stars', {
    state: getInitialState,
    getters: {
        isStarredById: (state) => (id: string): boolean => !!state.map[id]?.starred,
        getStarByItemId: (state) => (itemId: string): Star | undefined => state.map[itemId],
        getStarsByKind: (state) => (kind: string): Star[] => {
            if (state.fetching) return [];

            return Object.values(state.map).filter((star) => star.itemType === kind);
        },
    },
    actions: {
        setFetching (isFetching: boolean) {
            this.fetching = isFetching;
        },
        setMap (map: Record<string, Star>) {
            this.map = map;
        },
        setMapAtKey ({ id, star }: { id: string; star: Star }) {
            this.map[id] = star;
        },
        deleteByKey ({ id }: { id: string }) {
            delete this.map[id];
        },
        setError (error: unknown) {
            this.error = error;
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }
            this.setFetching(true);
            loadAllPromise = fetchStars().then(({ data: stars }) => {
                const decorated = (Array.isArray(stars) ? (stars as Star[]) : []).map((s) => ({ ...s, starred: true }));
                this.setMap(keyBy(decorated, 'itemId') as Record<string, Star>);
                this.setFetching(false);
            }).catch((error) => {
                loadAllPromise = null;
                this.setError(error);
                this.setFetching(false);
                throw error;
            });
            await loadAllPromise;
        },
        async loadOne ({ itemId, reFetch = false }: { itemId: string; reFetch?: boolean }) {
            if (this.getStarByItemId(itemId) && !reFetch) return;
            this.setFetching(true);
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const { data: star } = await fetchStar(itemId as any);
                this.setFetching(false);
                this.setMapAtKey({ id: itemId, star: star as Star });
            } catch (error) {
                this.setFetching(false);
                this.setError(error);
                throw error;
            }
        },
        async addNow (payload: { id: string; type: string }) {
            const star: Star = { itemId: payload.id, itemType: payload.type, starred: true };
            this.setMapAtKey({ id: payload.id, star });
            try {
                return await addStar({ itemType: payload.type, itemId: payload.id });
            } catch (error) {
                this.deleteByKey({ id: payload.id });
                throw error;
            }
        },
        async removeNow (payload: { id: string; type: string }) {
            this.deleteByKey({ id: payload.id });
            try {
                return await deleteStar({ itemType: payload.type, itemId: payload.id });
            } catch (error) {
                const star: Star = { itemId: payload.id, itemType: payload.type, starred: true };
                this.setMapAtKey({ id: payload.id, star });
                throw error;
            }
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useStarsStore, module.hot));
}
